import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
  Button, Loader
} from '@components/ui';
import * as OrderHelpers from '@helpers/OrderHelpers';
import * as ProductHelpers from '@helpers/ProductHelpers';
import { useAnalytics } from '@hooks/useAnalytics';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useConfig } from '@hooks/data/useConfig';
import { useOrderProductMutation } from '@hooks/data/useOrderProductMutation';

import styles from './ProductOnboardingCardActions.module.scss';

const ProductOnboardingCardActions = (props) => {

  const {
    className,
    disabled,
    order = {},
    product = {},
    listProps = {},
  } = props;

  // Hooks

  const analytics = useAnalytics();
  const { data: config } = useConfig();
  const isBreakpoint = useMediaQuery(`(max-width: ${styles.breakpoint})`);

  // Props

  const { errors } = config || {};

  const orderItem = React.useMemo(() => OrderHelpers.getOrderItem(order, product), [order, product]);
  const quantity = React.useMemo(() => OrderHelpers.getOrderItemQuantity(order, product), [order, product]);

  const { id: orderItemId } = orderItem || {};
  const { tokenValue: orderTokenValue } = order || {};

  // Hooks

  const [expanded, setExpanded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Mutation Hooks

  const {
    isError: isMutationError, add: addProduct, edit: editProductQuantity, remove: removeProduct,
  } = useOrderProductMutation();

  // Handlers

  // --- Add

  const handleAdd = React.useCallback((e) => {
    e.stopPropagation();
    setLoading(true);

    addProduct({
      product,
      listProps,
      bypassAddressRequirement: true,
      bypassQuantityVariants: true,
      order,
      onSuccess: () => {
        ProductHelpers.checkMaxQuantityReached(0, 1, product, errors, { analytics });
      },
      onSettled: () => {
        setExpanded(true);
        setLoading(false);
      },
    });
  }, [order, product]);

  // --- Edit

  const handleQuantityEdit = React.useCallback((oldQuantity, newQuantity) => {
    editProductQuantity({
      product,
      order: {
        tokenValue: orderTokenValue
      },
      orderItem: {
        id: orderItemId
      },
      previousQuantity: oldQuantity,
      quantity: newQuantity,
      listProps,
      onSuccess: () => {
        ProductHelpers.checkMaxQuantityReached(oldQuantity, newQuantity, product, errors, { analytics });
      },
    });
  }, [orderTokenValue, orderItemId, product]);

  // --- Remove

  const handleRemove = React.useCallback(() => {
    removeProduct({
      product,
      order: {
        tokenValue: orderTokenValue
      },
      orderItem: {
        id: orderItemId
      },
      listProps,
      onSuccess: () => {
        setExpanded(false);
        setLoading(false);
      }
    });
  }, [orderTokenValue, orderItemId, product]);

  const onQuantityChange = React.useCallback((oldQuantity, newQuantity) => {
    ProductHelpers.handleChangeQuantityClick(
      oldQuantity,
      newQuantity,
      handleRemove,
      handleQuantityEdit
    );
  }, [handleRemove, handleQuantityEdit]);

  // Debounced Hook

  const {
    value: quantityTarget, reset, decrease, increase
  } = useDebouncedValue(onQuantityChange, { ms: 600, initialValue: quantity });

  const [maxQuantity, errorMessage] = React.useMemo(() => (
    ProductHelpers.getMaxAllowedQuantity(product, errors)
  ), [product, errors]);

  // Flags

  const canIncreaseQuantity = maxQuantity > 0 && quantityTarget < maxQuantity;
  const canDecreaseQuantity = quantityTarget > 0;

  const inIncreaseDisabled = disabled || loading || !canIncreaseQuantity;
  const isDecreaseDisabled = disabled || loading || !expanded || !canDecreaseQuantity;

  // Effects

  React.useEffect(() => {
    if (expanded && quantityTarget === 0) {
      setExpanded(false);
      setLoading(true);
    }
  }, [expanded, quantityTarget]);

  React.useEffect(() => {
    if (quantity > 0) {
      setExpanded(true);
    }
  }, [quantity]);

  React.useEffect(() => {
    if (isMutationError) {
      reset();
    }
  }, [isMutationError]);

  // Render

  return (
    <div
      className={classnames(
        styles.root,
        { [styles.expanded]: expanded },
        { [styles.loading]: loading },
        className
      )}
    >

      {/* Left */}
      <div className={styles.left} />

      {/* Center */}
      <div className={styles.center} />

      {/* Right */}
      <div className={styles.right} />

      {/* Content */}
      <div className={styles.content}>

        {/* Loader */}
        <div className={styles.loader}>
          <Loader
            size={isBreakpoint ? 18 : 20}
            lineWeight={2}
          />
        </div>

        {/* Decrease */}
        <Button
          className={classnames(styles.icon, styles.decrease)}
          icon="minus"
          iconSize={isBreakpoint ? 20 : 24}
          iconStrokeWidth={2}
          disabled={isDecreaseDisabled}
          onClick={() => {
            decrease();
          }}
        />

        {/* Quantity */}
        <div className={styles.quantity}>
          <span className={styles.quantityText}>
            {quantityTarget}
          </span>
        </div>

        {/* Increase */}
        <Button
          className={classnames(styles.icon, styles.increase)}
          icon="plus"
          iconSize={isBreakpoint ? 20 : 22}
          iconStrokeWidth={2}
          title={inIncreaseDisabled && !loading ? errorMessage : null}
          disabled={inIncreaseDisabled}
          onClick={(e) => {
            if (quantity > 0) {
              increase();
            } else {
              handleAdd(e);
            }
          }}
        />
      </div>

    </div>
  );

};

ProductOnboardingCardActions.propTypes = {
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  disabled: PropTypes.bool,
  order: PropTypes.shape({}),
  product: PropTypes.shape({}),
  listProps: PropTypes.shape({
    analyticsListId: PropTypes.string,
    analyticsListName: PropTypes.string,
  }),
};

export default ProductOnboardingCardActions;
